import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const type = params.get("type") || "invite";

  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/auth/validate-token", { params: { token, type } })
      .then((res) => {
        if (res.data.valid) { setInfo(res.data); setState("valid"); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token, type]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/set-password", { token, password });
      setState("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not set your password.");
    } finally {
      setLoading(false);
    }
  };

  const isReset = type === "reset" || info?.type === "reset";
  const heading = isReset ? "Reset your password" : "Set your password";

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-linen px-6 py-16">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <span className="block h-px w-10 bg-brand-primary" />
          <span className="font-serif text-2xl text-stone-900">Building The Bridge</span>
        </div>

        {state === "checking" && <p className="text-stone-400 ai-thinking">Checking your link…</p>}

        {state === "invalid" && (
          <div data-testid="activate-invalid">
            <h2 className="font-serif text-3xl text-stone-900 mb-2">This link isn't valid</h2>
            <p className="text-stone-600 mb-6">It may have expired or already been used. Ask your coach for a fresh link, or reset your password from the login page.</p>
            <button onClick={() => navigate("/login")} className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white hover:bg-brand-hover transition-colors">
              Go to login <ArrowRight size={16} />
            </button>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} data-testid="activate-form">
            <div className="label-eyebrow mb-3">{info?.email}</div>
            <h2 className="font-serif text-4xl text-stone-900 mb-2">{heading}</h2>
            <p className="text-stone-600 mb-7">
              {isReset ? "Choose a new password to get back into your studio." : `Welcome${info?.name ? `, ${info.name.split(" ")[0]}` : ""}. Set a password to enter your studio.`}
            </p>
            <div className="space-y-5">
              <div>
                <label className="text-sm text-stone-600">New password</label>
                <input data-testid="activate-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" placeholder="at least 6 characters" />
              </div>
              <div>
                <label className="text-sm text-stone-600">Confirm password</label>
                <input data-testid="activate-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" placeholder="repeat password" />
              </div>
            </div>
            {error && <p data-testid="activate-error" className="mt-4 text-sm text-[#9E4A41]">{error}</p>}
            <button data-testid="activate-submit" type="submit" disabled={loading}
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white hover:bg-brand-hover transition-colors disabled:opacity-60">
              {loading ? "Saving…" : "Save and continue"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        )}

        {state === "done" && (
          <div data-testid="activate-done">
            <div className="mb-3 flex items-center gap-2 text-brand-sage">
              <Check size={20} /> <span className="font-serif text-3xl text-stone-900">You're all set</span>
            </div>
            <p className="text-stone-600 mb-6">Your password is saved. Sign in to enter your studio.</p>
            <button onClick={() => navigate("/login")} className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white hover:bg-brand-hover transition-colors">
              Go to login <ArrowRight size={16} />
            </button>
          </div>
        )}

        <div className="mt-10 flex items-center gap-2 text-xs text-stone-400">
          <ShieldCheck size={13} /> Your reflections stay private to you and your coach.
        </div>
      </div>
    </div>
  );
}
